import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
    fetchUserPoints,
    fetchTournaments
} from "../api/api.js";
import {
    getTournamentData,
    getTournamentDisplayName
} from "../utils/tournamentUtils";

const Points = () => {
    const { username } = useParams();
    const [pointsData, setPointsData] = useState(null);
    const [tournaments, setTournaments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [expandedRow, setExpandedRow] = useState(null);
    const [sortConfig, setSortConfig] = useState({ key: "time", direction: "desc" });
    const [selectedFilter, setSelectedFilter] = useState(() => {
        const saved = localStorage.getItem("points-filter");
        return saved && saved !== "all" ? saved : "all";
    });

    const sortedTournaments = [...tournaments].sort((a, b) => {
        if (a.season !== b.season) {
            return b.season.localeCompare(a.season);
        }

        return a.name.localeCompare(b.name);
    });

    const filterOptions = [
        { id: "all", name: "All" },
        { id: "disabled-tournaments", name: "-- Tournaments --" },

        ...sortedTournaments.map((tournament) => ({
            id: `tournament:${tournament.id}`,
            name: `${getTournamentDisplayName(tournament.name)} ${tournament.season}`
        }))
    ];

    const loadPoints = async (filter = "all", sortKey = "score", sortDirection = "desc") => {
        setLoading(true);
        setError(null);

        try {
            const data = await fetchUserPoints(username ?? null, filter, sortKey, sortDirection);
            setPointsData(data.data);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const initialize = async () => {
            const { tournaments } =
                await getTournamentData(
                    fetchTournaments
                );

            setTournaments(tournaments);
        };

        initialize();
    }, []);

    useEffect(() => {
        if (tournaments.length === 0) {
            return;
        }

        loadPoints(
            selectedFilter,
            sortConfig.key,
            sortConfig.direction
        );

    }, [selectedFilter, sortConfig, tournaments]);

    useEffect(() => {
        localStorage.setItem("points-filter", selectedFilter);
    }, [selectedFilter]);

    const handleSort = (key) => {
        const newDirection = sortConfig.key === key && sortConfig.direction === "desc" ? "asc" : "desc";
        setSortConfig({ key, direction: newDirection });
    };

    const onFilterChange = (value) => {
        setSelectedFilter(value);
    };

    const toggleExpand = (index) => {
        setExpandedRow(expandedRow === index ? null : index);
    };

    const formatTimestamp = (timestamp) => {
        const date = new Date(timestamp * 1000); // Convert to milliseconds
        const today = new Date();

        // Normalize today's date to 00:00:00 (midnight) for comparison
        today.setHours(0, 0, 0, 0);

        // Normalize the given timestamp to 00:00:00
        const dateOnly = new Date(date);
        dateOnly.setHours(0, 0, 0, 0);

        if (dateOnly.getTime() === today.getTime()) {
            // Show only time if it's today
            return new Intl.DateTimeFormat('en-GB', {
                hour: '2-digit',
                minute: '2-digit',
                hour12: false,
            }).format(date);
        } else {
            // Show only date if it's yesterday, older, tomorrow, or after
            return new Intl.DateTimeFormat('en-GB', {
                year: '2-digit',
                month: '2-digit',
                day: '2-digit',
            }).format(date);
        }
    };

    const determinePoints = (prediction) => {
        let points = {
            game_winner: '',
            halftime_winner: '',
            bonus_winner: '',
            goals_count: '',
            goals_difference: '',
            exact_result: ''
        };
        let predictions = {
            game_winner: '',
            halftime_winner: '',
            bonus_winner: '',
            goals_count: '',
            goals_difference: '',
            exact_result: ''
        };

        if (prediction.game_results == undefined) {
            return {
                points: points,
                predictions: predictions
            }
        }

        let halfFullGame = '';
        let difference = '';

        if (prediction.game_results["home-ht"] > prediction.game_results["away-ht"]) {
            points.halftime_winner = "Home won";
            halfFullGame += '1';
        } else if (prediction.game_results["home-ht"] < prediction.game_results["away-ht"]) {
            points.halftime_winner = "Away won";
            halfFullGame += '2';
        } else {
            points.halftime_winner = "Draw";
            halfFullGame += 'X';
        }
        halfFullGame += '-';

        if (prediction.game_results.home > prediction.game_results.away) {
            points.game_winner = "Home won";
            halfFullGame += '1';
            difference += "Home by " + (prediction.game_results.home - prediction.game_results.away) + " goals";
        } else if (prediction.game_results.home < prediction.game_results.away) {
            points.game_winner = "Away won";
            halfFullGame += '2';
            difference += "Away by " + (prediction.game_results.away - prediction.game_results.home) + " goals";
        } else {
            points.game_winner = "Draw";
            halfFullGame += 'X';
            difference += "Draw";
        }

        points.bonus_winner = halfFullGame;
        points.goals_count = prediction.game_results.home + prediction.game_results.away < 3 ? "0-2 goals" : "3+ goals";
        points.goals_difference = difference;
        points.exact_result = prediction.game_results.home + ":" + prediction.game_results.away;

        halfFullGame = prediction.predictions.halftime + '-';
        difference = '';

        if (prediction.predictions.home > prediction.predictions.away) {
            predictions.game_winner = "Home";
            halfFullGame += '1';
            difference += "Home by " + (prediction.predictions.home - prediction.predictions.away);
        } else if (prediction.predictions.home < prediction.predictions.away) {
            predictions.game_winner = "Away";
            halfFullGame += '2';
            difference += "Away by " + (prediction.predictions.away - prediction.predictions.home);
        } else {
            predictions.game_winner = "Draw";
            halfFullGame += 'X';
            difference += "draw";
        }

        if (prediction.predictions.halftime === "X") {
            predictions.halftime_winner = "Draw";
        } else if (prediction.predictions.halftime === "1") {
            predictions.halftime_winner = "Home";
        } else {
            predictions.halftime_winner = "Away";
        }

        predictions.bonus_winner = halfFullGame;
        predictions.goals_count = prediction.predictions.home + prediction.predictions.away < 3 ? "0-2 goals" : "3+ goals";
        predictions.goals_difference = difference;
        predictions.exact_result = prediction.predictions.home + ":" + prediction.predictions.away;

        return {
            points: points,
            predictions: predictions
        }
    }

    if (loading) return <p>Loading...</p>;
    if (error) return <p>Error: {error}</p>;
    if (!pointsData) return <p>No data available</p>;

    return (
        <div className="content-container">
            <div className="filter-dropdown-container">
                <select
                    value={selectedFilter}
                    onChange={(e) => onFilterChange(e.target.value)}
                    className="filter-dropdown points-dropdown"
                >
                    {filterOptions.map((option) => (
                        <option key={option.id} value={option.id} disabled={option.id.startsWith("disabled")}>
                            {option.name}
                        </option>
                    ))}
                </select>
            </div>

            <div className="user-points dark-main-container scroll-wrapper">
                <div className="user-info-containter">
                    <div className="flag-name-container">
                        <img
                            src={pointsData.country_img}
                            alt={pointsData.country}
                            title={pointsData.country}
                            className="user-country-flag"
                        />
                        <div className="name">
                            <span className="username"> {pointsData.username} </span>
                            {pointsData.name}
                        </div>
                    </div>
                    <div className="total-points">
                        {pointsData.total_score ?? 0} points
                    </div>
                </div>

                <div className="predictions-container">
                    <table>
                        <thead>
                            <tr>
                                <th onClick={() => handleSort("time")}>Time <span className={`sort-icon ${sortConfig.key === "time" ? sortConfig.direction : ""}`} /></th>
                                <th className="left">Game</th>
                                <th>Prediction</th>
                                <th onClick={() => handleSort("points")}>Points <span className={`sort-icon ${sortConfig.key === "points" ? sortConfig.direction : ""}`} /></th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {pointsData?.predictions?.length > 0 && pointsData.predictions.map((prediction, index) => {
                                const points = determinePoints(prediction);

                                return (
                                    <React.Fragment key={index}>
                                        <tr>
                                            <td>{formatTimestamp(prediction.game_start_time)}</td>
                                            <td className="match-info">
                                                {prediction.home_name}
                                                <img src={prediction.home_image} alt={prediction.home_name} className="team-img" />
                                                <span className="highlight">{(prediction.game_results !== null)
                                                    ? (`${prediction.game_results.home}:${prediction.game_results.away} (${prediction.game_results["home-ht"]}:${prediction.game_results["away-ht"]})`)
                                                    : ""}
                                                </span>
                                                <img src={prediction.away_image} alt={prediction.away_name} className="team-img" />
                                                {prediction.away_name}
                                            </td>
                                            <td><span className="highlight">{prediction.predictions === '?'
                                                ? '?'
                                                : prediction.predictions.home + ":" + prediction.predictions.away + " (" + prediction.predictions.halftime + ")" + " "}</span></td>
                                            <td><span className="highlight">{prediction.total}</span></td>
                                            <td className="expand-icon" onClick={() => toggleExpand(index)}>
                                                <span className={`sort-icon ${expandedRow === index ? "asc" : "desc"}`} />
                                            </td>
                                        </tr>
                                        {expandedRow === index && (
                                            <tr className="expanded-row">
                                                <td colSpan="2">
                                                    <div className="expanded-content">
                                                        {prediction.points?.game_winner != null && (<div>Game Winner: {points.points.game_winner}</div>)}
                                                        {prediction.points?.halftime_winner != null && (<div>Halftime: {points.points.halftime_winner}</div>)}
                                                        {prediction.points?.bonus_winner != null && (<div>Bonus: {points.points.bonus_winner}</div>)}
                                                        {prediction.points?.goals_count != null && (<div>Goals Count: {points.points.goals_count}</div>)}
                                                        {prediction.points?.goals_difference != null && (<div>Goals Difference: {points.points.goals_difference}</div>)}
                                                        {prediction.points?.exact_result != null && (<div>Exact Result: {points.points.exact_result}</div>)}
                                                    </div>
                                                </td>
                                                <td>
                                                    <div className="expanded-content">
                                                        {prediction.points?.game_winner != null && (<div>{points.predictions.game_winner}</div>)}
                                                        {prediction.points?.halftime_winner != null && (<div>{points.predictions.halftime_winner}</div>)}
                                                        {prediction.points?.bonus_winner != null && (<div>{points.predictions.bonus_winner}</div>)}
                                                        {prediction.points?.goals_count != null && (<div>{points.predictions.goals_count}</div>)}
                                                        {prediction.points?.goals_difference != null && (<div>{points.predictions.goals_difference}</div>)}
                                                        {prediction.points?.exact_result != null && (<div>{points.predictions.exact_result}</div>)}
                                                    </div>
                                                </td>
                                                <td>
                                                    <div className="expanded-content">
                                                        {prediction.points?.game_winner != null && (<div>{prediction.points == null ? '' : prediction.points.game_winner}</div>)}
                                                        {prediction.points?.halftime_winner != null && (<div>{prediction.points == null ? '' : prediction.points.halftime_winner}</div>)}
                                                        {prediction.points?.bonus_winner != null && (<div>{prediction.points == null ? '' : prediction.points.bonus_winner}</div>)}
                                                        {prediction.points?.goals_count != null && (<div>{prediction.points == null ? '' : prediction.points.goals_count}</div>)}
                                                        {prediction.points?.goals_difference != null && (<div>{prediction.points == null ? '' : prediction.points.goals_difference}</div>)}
                                                        {prediction.points?.exact_result != null && (<div>{prediction.points == null ? '' : prediction.points.exact_result}</div>)}
                                                    </div>
                                                </td>
                                                <td colSpan="1"></td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}

export default Points;
